import { Bell, Mail, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@shared/components/ui/shadcn/switch';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import type { NotificationPreference } from '@modules/Notifications/models/Notification.types';

interface PreferenceState {
  inApp: boolean;
  email: boolean;
}

interface NotificationPreferencesTableProps {
  preferences: NotificationPreference[];
  preferencesState: Record<number, PreferenceState>;
  isPending?: boolean;
  onToggle: (pref: NotificationPreference, channel: 'inApp' | 'email') => void;
}

export function NotificationPreferencesTable({
  preferences,
  preferencesState,
  isPending = false,
  onToggle,
}: NotificationPreferencesTableProps) {
  const { t } = useTranslation('notificationsModule');

  return (
    <div
      className={`${configurationPageClass} pb-6 transition-opacity duration-300 ${isPending ? 'pointer-events-none opacity-90' : ''}`}
      data-testid="notification-preferences"
    >
      <div className="shrink-0">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t('notifications.preferences.pageTitle')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('notifications.preferences.pageDescription')}</p>
      </div>

      {preferences.length === 0 ? (
        <p className={`${configurationPageContentClass} text-sm text-muted-foreground`}>
          {t('notifications.preferences.noPreferences')}
        </p>
      ) : (
        <div className={`${configurationPageContentClass} overflow-y-auto`}>
          <div className="shrink-0 overflow-hidden rounded-lg border border-border bg-background">
            <div className="grid grid-cols-[1fr_4.5rem_4.5rem] items-center gap-3 border-b border-border bg-muted/30 px-4 py-3 sm:grid-cols-[1fr_5rem_5rem]">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Bell className="h-4 w-4 shrink-0 text-icons" />
                <span>{t('notifications.preferences.notifyAbout')}</span>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Mail className="h-3.5 w-3.5 text-icons" />
                <span className="hidden sm:inline">{t('notifications.preferences.email')}</span>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Smartphone className="h-3.5 w-3.5 text-icons" />
                <span className="hidden sm:inline">{t('notifications.preferences.inApp')}</span>
              </div>
            </div>

            {preferences.map((pref, index) => {
              const title =
                pref.notification_type?.name ||
                pref.notification_type?.in_app_title ||
                `Type ${pref.notification_type_id}`;
              const description = pref.notification_type?.description;

              return (
                <div
                  key={pref.id}
                  className={`grid grid-cols-[1fr_4.5rem_4.5rem] items-center gap-3 px-4 py-4 sm:grid-cols-[1fr_5rem_5rem] ${
                    index < preferences.length - 1 ? 'border-b border-border' : ''
                  }`}
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-sm font-medium text-foreground">{title}</p>
                    {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
                  </div>

                  <div className="flex justify-center">
                    <Switch
                      checked={preferencesState[pref.id]?.email ?? false}
                      onCheckedChange={() => onToggle(pref, 'email')}
                      aria-label={`${title} - ${t('notifications.preferences.email')}`}
                      data-testid={`notification-preference-${pref.id}-email`}
                    />
                  </div>

                  <div className="flex justify-center">
                    <Switch
                      checked={preferencesState[pref.id]?.inApp ?? false}
                      onCheckedChange={() => onToggle(pref, 'inApp')}
                      aria-label={`${title} - ${t('notifications.preferences.inApp')}`}
                      data-testid={`notification-preference-${pref.id}-in-app`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
